// Grace companion UI (practice-loop-v2): full-screen first pick, header
// avatar switch sheet, and the two passive effect overlays.

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { COMPANIONS, companionById, type CompanionId, type PackId } from '@skena/schema'

// Same hardcoded pack classes as PracticePage (N=3 rule: no theme system).
const PACK_BG: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-bg)]',
  nativity: 'bg-[var(--color-nativity-bg)]',
}

function CompanionCard({
  companion,
  onPick,
}: {
  companion: (typeof COMPANIONS)[number]
  onPick: (id: CompanionId) => void
}) {
  return (
    <motion.button
      type="button"
      onClick={() => onPick(companion.id)}
      whileTap={{ scale: 0.97 }}
      className="flex w-full items-center gap-4 rounded-[var(--radius-soft)] border-2 border-black/10 bg-white p-4 text-left shadow-[var(--shadow-card)] transition-colors hover:border-[var(--color-decoration)]"
    >
      <img
        src={companion.cardUrl}
        alt=""
        className="h-24 w-24 shrink-0 rounded-xl object-cover"
        loading="eager"
      />
      <span className="flex flex-col gap-1">
        <span className="text-lg font-bold text-[var(--color-fg)]">{companion.nameZh}</span>
        <span className="text-sm text-[var(--color-fg)]">{companion.passiveZh}</span>
        <span className="text-xs text-[var(--color-muted)]">
          「{companion.verseZh}」（{companion.scriptureRef}）
        </span>
      </span>
    </motion.button>
  )
}

/** Full-screen first-time picker — renders instead of the practice round. */
export function CompanionSelect({
  packId,
  onPick,
}: {
  packId: PackId
  onPick: (id: CompanionId) => void
}) {
  return (
    <main className={`mx-auto flex min-h-full max-w-2xl flex-col justify-center gap-6 p-6 ${PACK_BG[packId]}`}>
      <div className="text-center">
        <h1 className="font-[family-name:var(--font-display)] text-2xl font-black text-[var(--color-fg)]">
          選一位同伴陪你練習
        </h1>
        <p className="mt-1 text-sm text-[var(--color-muted)]">之後隨時可以換</p>
      </div>
      <div className="flex flex-col gap-3">
        {COMPANIONS.map((c) => (
          <CompanionCard key={c.id} companion={c} onPick={onPick} />
        ))}
      </div>
    </main>
  )
}

/** Bottom sheet for mid-session switching (spec: Companion switch entry). */
export function CompanionSheet({
  open,
  onClose,
  onPick,
}: {
  open: boolean
  onClose: () => void
  onPick: (id: CompanionId) => void
}) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-40 flex items-end justify-center bg-black/30"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-label="更換同伴"
            className="w-full max-w-2xl rounded-t-3xl bg-[var(--color-bg)] p-5 pb-8"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            onClick={(ev) => ev.stopPropagation()}
          >
            <p className="mb-3 text-center text-base font-bold text-[var(--color-fg)]">更換同伴</p>
            <div className="flex flex-col gap-3">
              {COMPANIONS.map((c) => (
                <CompanionCard key={c.id} companion={c} onPick={onPick} />
              ))}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

/** Header avatar button that opens the switch sheet. */
export function CompanionAvatarButton({
  companionId,
  onOpen,
}: {
  companionId: CompanionId
  onOpen: () => void
}) {
  const companion = companionById(companionId)
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`同伴：${companion.nameZh}，點擊更換`}
      className="flex items-center gap-1 rounded-full border border-black/10 bg-white/80 p-0.5 pr-2 shadow-sm transition-transform hover:-translate-y-0.5"
    >
      <img src={companion.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
      <span className="text-xs font-semibold text-[var(--color-fg)]">{companion.nameZh}</span>
    </button>
  )
}

/** Eagle passive overlay — the retried question soars. */
export function EagleSoar({ triggerKey }: { triggerKey: number }) {
  const reducedMotion = useReducedMotion()
  const eagle = companionById('eagle')
  if (reducedMotion) return null
  return (
    <motion.img
      key={triggerKey}
      src={eagle.avatarUrl}
      alt=""
      aria-hidden="true"
      className="pointer-events-none fixed z-30 h-14 w-14 rounded-full object-cover shadow-lg"
      initial={{ left: '-10%', top: '65%', opacity: 0, rotate: -8 }}
      animate={{ left: '105%', top: '18%', opacity: [0, 1, 1, 0.9], rotate: 6 }}
      transition={{ duration: 1.1, ease: [0.3, 0.6, 0.4, 1] }}
    />
  )
}
